import React, { BaseSyntheticEvent, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Box, FormControlLabel, Stack, Switch, Typography, styled, TextField } from '@mui/material';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import useAuth from '../../../providers/Auth/Auth.provider';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import { NotificationsContext } from '../../../providers/Notifications/Notifications.provider';
import useSettings, { SettingsSection } from '../../../providers/Settings/Settings.provider';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import './invoicing.scss';
import { useGetInvoiceServicesQuery } from '../../../redux/api/invoiceService/invoiceServiceApi';
import {
  InvoiceService,
  InvoiceServiceUnit,
  getInvoiceServiceUnitLabel
} from '../../../redux/api/invoiceService/invoiceService.model';
import TwAutocomplete, {
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import {
  DefaultInvoiceDate,
  DefaultInvoiceEmailBodyCopy,
  FactoringCompany,
  InvoiceSubjectLine,
  TenantAccountingSettings
} from '../../../providers/User/User.model';
import InvoiceUtils from '../../../utils/invoiceUtils';
import { useGetDivisionsQuery } from '../../../redux/api/divisions/divisionsApi';
import {
  useGetTenantAccountingSettingsQuery,
  useUpsertTenantAccountingSettingsMutation
} from '../../../redux/api/tenant/tenantApi';
import InvoicesService from '../../../providers/Invoices/Invoices.service';
import { InvoiceSources, QBOCustomField } from '../../../providers/Invoices/Invoices.model';
import { ReactComponent as QBLogo } from '../../../assets/icons/QBO-icon.svg';
import QBOService from '../../../providers/Quickbooks/QBO.service';
import { redirect } from '../../../utils/redirectUtils';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import { useAppDispatch } from '../../../redux/hooks';
import { twApi } from '../../../redux/api/api';
import Factoring from './Factoring/Factoring';
import FactoringService from '../../../providers/Factoring/Factoring.service';
import {
  FactoringOption,
  IFactoringIntegrations,
  INamedCompanies,
  IOtherCompanies
} from '../../../redux/api/factoring/factoring.model';
import _ from 'lodash';
import {
  useCreateInternalFactoringMutation,
  useDeleteInternalFactoringCompanyMutation,
  useGetInternalFactoringCompanyQuery
} from '../../../redux/api/internalFactoring/internalFactoringApi';
import FactoringUtils from '../../../utils/factoringUtils';
import TwTagsField from '../../../components/TwTagsField/TwTagsField';
import QbDesktopService from '../../../providers/Quickbooks/QbDesktop.service';
import TwQbDesktopFieldMapping from '../../../components/TwQbDesktop/TwQbDesktopFieldMapping';
import { QbDesktopFieldMapping } from '../../../providers/Quickbooks/QbDesktop.model';
import FuelcardAccountManagerSetting from './Fuelcard/FuelcardAccountManager';
import DownloadIcon from '@mui/icons-material/Download';
import { CircularProgress } from '@mui/material';
import { useGetQbDesktopConfigQuery } from '../../../redux/api/qbDesktop/qbDesktopApi';
import ErrorIcon from '@mui/icons-material/Error';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import TwSwitch from '../../../components/TwSwitch/TwSwitch';
import SalesTaxSettings from './SalesTax/SalesTaxSettings';
import TwDivider from '../../../components/TwDivider/TwDivider';

interface ITenantDefaultRecipients {
  to_cust_contact_email: boolean;
  to_cust_billing_email: boolean;
  cc_user_email: boolean;
  cc_accounting_email: boolean;
}

interface IInvoiceDefaultDescription {
  show_pickup_stops: boolean;
  show_drop_off_stops: boolean;
  show_transfer_stops: boolean;
  show_unspecific_stops: boolean;
  show_truckbase_load_number: boolean;
  show_customer_load_number: boolean;
  show_load_dates: boolean;
  show_stop_name: boolean;
  show_stop_address: boolean;
  show_pickup_and_delivery_numbers: boolean;
  show_commodities: boolean;
  show_trucks: boolean;
  show_trailers: boolean;
  show_drivers: boolean;
  show_total_empty_miles: boolean;
  show_total_loaded_miles: boolean;
  show_tags: boolean;
  show_order_notes: boolean;
}

export interface SettingsQuery {
  query: string | undefined;
}

const invoiceSubjectLineOptions: TwAutocompleteOption[] = Object.keys(InvoiceSubjectLine).map(
  (key) => ({
    id: key,
    label: InvoiceUtils.getInvoiceSubjectLineLabel(key as InvoiceSubjectLine),
    value: key
  })
);

const defaultInvoiceDateOptions: TwAutocompleteOption[] = Object.keys(DefaultInvoiceDate).map(
  (key) => ({
    id: key,
    label: InvoiceUtils.getDefaultInvoiceDateLabel(key as DefaultInvoiceDate),
    value: key
  })
);

const InvoicingAndAccounting: React.FC<SettingsQuery> = ({ query }): JSX.Element => {
  const {
    data: { userData },
    getTenantSetting,
    refreshCurrentUser
  } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    enableQboSetCustomLoadField,
    enableFactoring,
    enableQbDesktop,
    enableFuelTransactions,
    enableFuelcardIntegration
  } = useFeatureFlags();
  const dispatch = useAppDispatch();
  const showCommoditiesDescriptionCheckbox = getTenantSetting('load-commodities');

  const divisionsEnabled =
    (userData?.tenantSettings && userData.tenantSettings['divisions']) || false;

  const [divisionId, setDivisionId] = useState<string>(userData!.defaultDivision?.id);
  const [tenantAccountingSettings, setTenantAccountingSettings] =
    useState<TenantAccountingSettings>();

  const noneOption: FactoringOption = {
    id: 'none',
    label: 'None',
    value: 'none',
    requiredFields: []
  };
  const [factoringCompanies, setFactoringCompanies] = useState<FactoringOption[]>([]);
  const [factoringCompany, setFactoringCompany] = useState<FactoringOption>(noneOption);
  const [companyData, setCompanyData] = useState<INamedCompanies | IOtherCompanies | null>(null);
  const [originalCompanyData, setOriginalCompanyData] = useState<
    INamedCompanies | IOtherCompanies | null
  >(null);
  const [loadingFactoring, setLoadingFactoring] = useState(true);
  const [sendAllInvoices, setSendAllInvoices] = useState(false);
  const [hasErrorFactoring, setHasErrorFactoring] = useState(false);
  const [hasFactoringCompanies, setHasFactoringCompanies] = useState({
    internal: false,
    external: false
  });
  const [isQbDesktopFieldMappingOpen, setIsQbDesktopFieldMappingOpen] = useState(false);
  const {
    data: qbDesktopConfig,
    refetch: refetchQbDesktopConfig,
    isFetching: isQbDesktopConfigFetching
  } = useGetQbDesktopConfigQuery({ divisionId: divisionId }, { skip: !divisionId });
  const [qbDesktopDivisionFieldMapping, setQbDesktopDivisionFieldMapping] =
    useState<QbDesktopFieldMapping>({});
  const [isQbDesktopDlObjInfoLoading, setIsQbDesktopDlObjInfoLoading] = useState(false);
  const [isTbDlObjInfoLoading, setIsTbDlObjInfoLoading] = useState(false);
  const [isQbDesktopAutoSyncEnabled, setIsQbDesktopAutoSyncEnabled] = useState<boolean | undefined>(
    undefined
  );
  const [isQbDesktopAutoCustomerCreateEnabled, setIsQbDesktopAutoCustomerCreateEnabled] = useState<
    boolean | undefined
  >(undefined);
  const showQboCustomField =
    enableQboSetCustomLoadField && !!tenantAccountingSettings?.division_qbo_credentials?.id;

  const isQboEnabled = !!tenantAccountingSettings?.division_qbo_credentials?.id;

  const isSignedInQbo = !!tenantAccountingSettings?.division_qbo_credentials?.id;

  const { data: divisions = { content: [] }, refetch: refetchDivisionsData } = useGetDivisionsQuery(
    {}
  );
  const { data: fetchedSettings, refetch: refetchAccountingSettings } =
    useGetTenantAccountingSettingsQuery({ divisionId }, { skip: !divisionId });
  const [upsertTenantAccountSettings] = useUpsertTenantAccountingSettingsMutation();

  const {
    data: invoiceServicesData,
    requestId: invoiceServicesRequestId,
    isFetching: servicesFetching
  } = useGetInvoiceServicesQuery(
    {
      queryParams: { source: isQboEnabled ? InvoiceSources.QBO : InvoiceSources.TB },
      filters: { divisionId: divisionId }
    },
    {
      selectFromResult: ({ data = { content: [], total: 0 }, requestId, isFetching }) => ({
        data: {
          content: data.content
            .filter((service) => service.unit !== InvoiceServiceUnit.PERCENTAGE)
            .map((item) => ({
              id: item.id,
              label: getInvoiceServiceUnitLabel(item.name),
              value: item
            })),
          total: data.total
        },
        requestId,
        isFetching
      })
    }
  );

  const [selectedDefaultInvoiceService, setSelectedDefaultInvoiceService] = useState<
    TwAutocompleteOption | null | undefined
  >(null);

  const [selectedInvoiceEmailSubjectLine, setSelectedInvoiceEmailSubjectLine] = useState<
    TwAutocompleteOption | null | undefined
  >(null);

  const [selectedDefaultInvoiceDate, setSelectedDefaultInvoiceDate] = useState<
    TwAutocompleteOption | null | undefined
  >(null);

  const [accountingEmail, setAccountingEmail] = useState<string>(
    tenantAccountingSettings?.accounting_email || ''
  );
  const [defaultRecipients, setDefaultRecipients] = useState<ITenantDefaultRecipients>({
    to_cust_contact_email: false,
    to_cust_billing_email: false,
    cc_user_email: false,
    cc_accounting_email: false
  });
  const [additionalRecipients, setAdditionalRecipients] = useState<string[]>([]);
  const [defaultInvoiceDescription, setDefaultInvoiceDescription] =
    useState<IInvoiceDefaultDescription>({
      show_pickup_stops: false,
      show_drop_off_stops: false,
      show_transfer_stops: false,
      show_unspecific_stops: false,
      show_customer_load_number: false,
      show_stop_name: false,
      show_stop_address: false,
      show_load_dates: false,
      show_pickup_and_delivery_numbers: false,
      show_truckbase_load_number: false,
      show_commodities: false,
      show_trucks: false,
      show_trailers: false,
      show_drivers: false,
      show_total_empty_miles: false,
      show_total_loaded_miles: false,
      show_tags: false,
      show_order_notes: false
    });
  const [useDivisionPrefix, setUseDivisionPrefix] = useState<boolean>(
    (tenantAccountingSettings?.invoice_prefix &&
      tenantAccountingSettings?.invoice_prefix !== null) ||
      false
  );
  const [invoicePrefix, setInvoicePrefix] = useState<string | null>(
    tenantAccountingSettings?.invoice_prefix || null
  );
  const [defaultInvoiceSendOption, setDefaultInvoiceSendOption] = useState<boolean>(
    tenantAccountingSettings?.select_invoice_send_option || false
  );
  const [salesTaxEnabled, setSalesTaxEnabled] = useState<boolean>(
    tenantAccountingSettings?.sales_tax_enabled || false
  );
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [hasFactoringChanges, setHasFactoringChanges] = useState<boolean>(false);
  const [qboCustomFieldsList, setQboCustomFieldsList] = useState<TwAutocompleteOption[]>([]);
  const [selectedQboCustomField, setSelectedQboCustomField] = useState<
    TwAutocompleteOption | null | undefined
  >(null);
  const [defaultCustomField, setDefaultCustomField] = useState<
    TwAutocompleteOption | null | undefined
  >(null);

  const [invoiceEmailBody, setInvoiceEmailBody] = useState<string>(
    tenantAccountingSettings?.invoice_email_body || DefaultInvoiceEmailBodyCopy
  );

  const { addNotification } = useContext(NotificationsContext);
  const { updateSettingsTab } = useSettings();

  const { data: fetchedInternalFactoring, refetch: refetchInternalFactoring } =
    useGetInternalFactoringCompanyQuery({ divisionId }, { skip: !divisionId });

  const [createInternalFactoring] = useCreateInternalFactoringMutation();
  const [deleteInternalFactoring] = useDeleteInternalFactoringCompanyMutation();
  const [prefixError, setPrefixError] = useState<string | null>(null);
  const prefixRef = useRef<HTMLInputElement | undefined>();

  const [qbButtonChoice, setQbButtonChoice] = useState<
    'LOADING' | 'QB_ONLINE' | 'QB_DESKTOP' | 'ALL'
  >('LOADING');

  const qbDesktopSettings = useMemo(() => {
    return {
      [divisionId]: qbDesktopConfig
    };
  }, [divisionId, qbDesktopConfig]);

  useEffect(() => {
    if (!tenantAccountingSettings) return;

    // only fetch field mapping if connected
    if (qbDesktopSettings[divisionId]?.doesConfigExistInDb) {
      setIsQbDesktopAutoSyncEnabled(!!qbDesktopSettings[divisionId]?.config?.is_auto_sync_enabled);
      setIsQbDesktopAutoCustomerCreateEnabled(
        !!qbDesktopSettings[divisionId]?.config?.is_auto_customer_create_enabled
      );
      QbDesktopService.getFieldMapping(divisionId)
        .then((fieldMapping) => {
          setQbDesktopDivisionFieldMapping(fieldMapping);
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // if quickbooks desktop feature is enabled
    if (enableQbDesktop) {
      if (isQbDesktopConfigFetching) {
        // if still fetching connection data
        setQbButtonChoice('LOADING');
      } else if (
        // if settings are loaded in but nothing is connected
        tenantAccountingSettings &&
        !tenantAccountingSettings?.division_qbo_credentials?.id &&
        qbDesktopSettings[divisionId] &&
        !qbDesktopSettings[divisionId]?.doesConfigExistInDb
      ) {
        // if settings are loaded in but nothing is connected
        setQbButtonChoice('ALL');
      } else if (tenantAccountingSettings?.division_qbo_credentials?.id) {
        // if quickbooks online is connected
        setQbButtonChoice('QB_ONLINE');
      } else if (qbDesktopSettings[divisionId]?.doesConfigExistInDb) {
        // if quickbooks desktop is connected
        setQbButtonChoice('QB_DESKTOP');
      } else {
        // fallback to loading
        setQbButtonChoice('LOADING');
      }
    } else {
      setQbButtonChoice('QB_ONLINE');
    }
  }, [divisionId, enableQbDesktop, tenantAccountingSettings, qbDesktopSettings]);

  useEffect(() => {
    setTenantAccountingSettings(fetchedSettings);
  }, [fetchedSettings]);

  useEffect(() => {
    if (isQboEnabled) {
      InvoicesService.getQBOCustomFields(
        tenantAccountingSettings?.division_qbo_credentials?.division_id
      )
        .then((response) => {
          setQboCustomFieldsList(
            response.map((item: QBOCustomField) => ({
              id: item.StringValue,
              label: item.StringValue,
              value: item
            }))
          );
        })
        .catch((error) => {
          addNotification({
            message: error.message || 'Error fetching Quickbooks custom fields',
            variant: 'error'
          });
        });
    }
  }, [isQboEnabled, tenantAccountingSettings?.division_qbo_credentials?.division_id]);

  useEffect(() => {
    if (!tenantAccountingSettings) return;

    const { invoice_description } = tenantAccountingSettings;

    setDefaultRecipients({
      to_cust_contact_email: tenantAccountingSettings.to_cust_contact_email,
      to_cust_billing_email: tenantAccountingSettings.to_cust_billing_email,
      cc_user_email: tenantAccountingSettings.cc_user_email,
      cc_accounting_email: tenantAccountingSettings.cc_accounting_email
    });

    setAdditionalRecipients(tenantAccountingSettings.additional_recipients || []);
    setAccountingEmail(tenantAccountingSettings.accounting_email);
    setInvoicePrefix(tenantAccountingSettings.invoice_prefix);
    setUseDivisionPrefix(
      (tenantAccountingSettings?.invoice_prefix &&
        tenantAccountingSettings?.invoice_prefix !== null) ||
        false
    );
    setDefaultInvoiceSendOption(tenantAccountingSettings?.select_invoice_send_option || false);
    const invoiceSubjectLine = invoiceSubjectLineOptions.find(
      (item) => item.value === tenantAccountingSettings.invoice_subject_line
    );
    const defaultInvoiceDate = defaultInvoiceDateOptions.find(
      (item) => item.value === tenantAccountingSettings.default_invoice_date
    );

    setSelectedInvoiceEmailSubjectLine(invoiceSubjectLine);

    setSelectedDefaultInvoiceDate(defaultInvoiceDate);

    setDefaultInvoiceDescription({
      show_pickup_stops: invoice_description.show_pickup_stops,
      show_drop_off_stops: invoice_description.show_drop_off_stops,
      show_transfer_stops: invoice_description.show_transfer_stops,
      show_unspecific_stops: invoice_description.show_unspecific_stops,
      show_customer_load_number: invoice_description.show_customer_load_number,
      show_stop_name: invoice_description.show_stop_name,
      show_stop_address: invoice_description.show_stop_address,
      show_load_dates: invoice_description.show_load_dates,
      show_pickup_and_delivery_numbers: invoice_description.show_pickup_and_delivery_numbers,
      show_truckbase_load_number: invoice_description.show_truckbase_load_number,
      show_commodities: showCommoditiesDescriptionCheckbox
        ? invoice_description.show_commodities
        : false,
      show_trucks: invoice_description.show_trucks,
      show_trailers: invoice_description.show_trailers,
      show_drivers: invoice_description.show_drivers,
      show_total_empty_miles: invoice_description.show_total_empty_miles,
      show_total_loaded_miles: invoice_description.show_total_loaded_miles,
      show_tags: invoice_description.show_tags,
      show_order_notes: invoice_description.show_order_notes
    });

    setSendAllInvoices(tenantAccountingSettings.send_invoices_to_factoring_by_default);
    setInvoiceEmailBody(tenantAccountingSettings.invoice_email_body || DefaultInvoiceEmailBodyCopy);
    setSalesTaxEnabled(tenantAccountingSettings?.sales_tax_enabled || false);

    if (isQboEnabled && qboCustomFieldsList.length > 0) {
      const qboCustomField = qboCustomFieldsList.find(
        (field: { id: any }) =>
          field.id === tenantAccountingSettings.qbo_customer_number_custom_field
      );

      if (qboCustomField) {
        setSelectedQboCustomField(qboCustomField);
        setDefaultCustomField(qboCustomField);
      }
    }
  }, [tenantAccountingSettings, isQboEnabled, qboCustomFieldsList]);

  useEffect(() => {
    if (invoiceServicesData.content.length === 0 || servicesFetching) return;
    const defaultService = invoiceServicesData.content.find(
      (item: { value: InvoiceService }) => (item.value as InvoiceService)?.isDefault
    );

    setSelectedDefaultInvoiceService(defaultService || null);
  }, [servicesFetching, invoiceServicesRequestId, divisionId]);

  const getFactoringCompanyToOption = (
    allIntegrations: FactoringOption[],
    integration: string
  ): FactoringOption => {
    const company = allIntegrations.find((company) => company.id === integration);

    if (!company) return noneOption;

    return company;
  };

  const getFactoringConfig = async (
    internalCompany: FactoringCompany | undefined
  ): Promise<void> => {
    setHasFactoringCompanies({ external: false, internal: false });
    setHasErrorFactoring(false);
    let integrations: IFactoringIntegrations[] = [];
    let allIntegrations: FactoringOption[] = [];

    try {
      integrations = await FactoringService.getIntegrations();

      const integrationOptions: FactoringOption[] = integrations.map((integration) => ({
        id: integration.company,
        label: FactoringUtils.capitalizeFirstLetterCompany(integration.company),
        value: integration,
        requiredFields: integration.requiredFields
      }));

      allIntegrations = [
        noneOption,
        ...integrationOptions,
        { id: 'other', label: 'Other', value: 'other', requiredFields: [] }
      ];

      setFactoringCompanies(allIntegrations);
    } catch (error) {
      console.error(error);
      setHasErrorFactoring(true);
    }

    FactoringService.getConfig(divisionId)
      .finally(() => setLoadingFactoring(false))
      .then((response) => {
        if (!response) {
          setCompanyData(null);
          setOriginalCompanyData(null);
          return;
        }

        setCompanyData(response);
        setOriginalCompanyData(response);
        setHasFactoringCompanies({ ...hasFactoringCompanies, external: true });

        if (response.company) {
          const companyToOption = getFactoringCompanyToOption(allIntegrations, response.company);

          setFactoringCompany(companyToOption);
        }
      })
      .catch((error) => {
        if (error.status === 404) {
          if (internalCompany) {
            // if the user has internal factoring config then set "other" option
            setInternalCompany(allIntegrations, internalCompany);
            return;
          }

          setCompanyData(null);
          setOriginalCompanyData(null);
          factoringCompanies.length !== 0 && setFactoringCompany(allIntegrations[0]);

          return;
        }

        console.log('error', error);
        addNotification({
          message: error.data,
          variant: 'error'
        });
      });
  };

  const setInternalCompany = (
    allIntegrations: FactoringOption[],
    fetchedInternalFactoring: FactoringCompany
  ): void => {
    setFactoringCompany(allIntegrations[allIntegrations.length - 1]);

    const company = {
      company: fetchedInternalFactoring.name,
      company_email: fetchedInternalFactoring.email_address,
      company_address: fetchedInternalFactoring.address
    };

    setCompanyData(company);
    setOriginalCompanyData(company);
    setHasFactoringCompanies({ ...hasFactoringCompanies, internal: true });
  };

  const postFactoringConfig = async (): Promise<void> => {
    if (companyData === null) return;

    const data = {
      ..._.omitBy(companyData, _.isEmpty),
      company: factoringCompany.id
    };

    FactoringService.postConfig(divisionId, data)
      .then(() => {
        getFactoringConfig(fetchedInternalFactoring);
        addNotification({
          message: 'Factoring company setup successfully',
          variant: 'success'
        });
      })
      .catch((error) => {
        setLoadingFactoring(false);
        resetFactoring();
        addNotification({
          message: error ? error : "Couldn't setup factoring company",
          variant: 'error'
        });
      });
  };

  const deleteFactoringConfig = async (): Promise<void> => {
    FactoringService.deleteConfig(divisionId)
      .finally(() => setLoadingFactoring(false))
      .then(() => {
        setCompanyData(null);
        setOriginalCompanyData(null);

        addNotification({
          message: 'Factoring company setup removed successfully',
          variant: 'success'
        });
      })
      .catch((error) => {
        resetFactoring();
        addNotification({
          message: error ? error : "Couldn't remove factoring company",
          variant: 'error'
        });
      });
  };

  const saveFactoringConfig = async (): Promise<void> => {
    setLoadingFactoring(true);
    if (factoringCompany.id === 'none') {
      if (fetchedInternalFactoring) {
        await handleDeleteInternalFactoring();
      }

      if (hasFactoringCompanies.external) {
        await deleteFactoringConfig();
      }

      return;
    }

    if (!fetchedInternalFactoring && factoringCompany.id === 'other') {
      createInternalFactoringCompany();
      return;
    }

    postFactoringConfig();
  };

  const handleDeleteInternalFactoring = async (): Promise<void> => {
    deleteInternalFactoring(divisionId)
      .then(() => {
        addNotification({
          message: 'Internal factoring company deleted successfully',
          variant: 'success'
        });
      })
      .catch((error: { data: { message: any } }) => {
        addNotification({
          message: error.data.message,
          variant: 'error'
        });
      });
  };

  useEffect(() => {
    if (!enableFactoring) return;

    setLoadingFactoring(true);
    setFactoringCompany(noneOption);
    setCompanyData(null);
    setOriginalCompanyData(null);
    refetchInternalFactoring();

    if (!fetchedInternalFactoring) {
      getFactoringConfig(fetchedInternalFactoring);
    }
  }, [divisionId, enableFactoring]);

  useEffect(() => {
    if (!enableFactoring) return;

    getFactoringConfig(fetchedInternalFactoring);
  }, [fetchedInternalFactoring, refetchInternalFactoring]);

  const handleEmailChange = (e: BaseSyntheticEvent): void => {
    setAccountingEmail(e.target.value);
  };

  const discardChanges = (): void => {
    setAccountingEmail(tenantAccountingSettings?.accounting_email || '');
    setAdditionalRecipients(tenantAccountingSettings?.additional_recipients || []);
    setDefaultRecipients({
      to_cust_contact_email: tenantAccountingSettings?.to_cust_contact_email || false,
      to_cust_billing_email: tenantAccountingSettings?.to_cust_billing_email || false,
      cc_user_email: tenantAccountingSettings?.cc_user_email || false,
      cc_accounting_email: tenantAccountingSettings?.cc_accounting_email || false
    });
    setInvoicePrefix(tenantAccountingSettings?.invoice_prefix || null);
    setUseDivisionPrefix(
      (tenantAccountingSettings?.invoice_prefix &&
        tenantAccountingSettings?.invoice_prefix !== null) ||
        false
    );
    setDefaultInvoiceSendOption(tenantAccountingSettings?.select_invoice_send_option || false);
    setDefaultInvoiceDescription({
      show_pickup_stops: tenantAccountingSettings?.invoice_description?.show_pickup_stops || false,
      show_drop_off_stops:
        tenantAccountingSettings?.invoice_description?.show_drop_off_stops || false,
      show_transfer_stops:
        tenantAccountingSettings?.invoice_description?.show_transfer_stops || false,
      show_unspecific_stops:
        tenantAccountingSettings?.invoice_description?.show_unspecific_stops || false,
      show_customer_load_number:
        tenantAccountingSettings?.invoice_description?.show_customer_load_number || false,
      show_stop_name: tenantAccountingSettings?.invoice_description?.show_stop_name || false,
      show_stop_address: tenantAccountingSettings?.invoice_description?.show_stop_address || false,
      show_load_dates: tenantAccountingSettings?.invoice_description?.show_load_dates || false,
      show_pickup_and_delivery_numbers:
        tenantAccountingSettings?.invoice_description?.show_pickup_and_delivery_numbers || false,
      show_truckbase_load_number:
        tenantAccountingSettings?.invoice_description?.show_truckbase_load_number || false,
      show_commodities: tenantAccountingSettings?.invoice_description?.show_commodities || false,
      show_trucks: tenantAccountingSettings?.invoice_description?.show_trucks || false,
      show_trailers: tenantAccountingSettings?.invoice_description?.show_trailers || false,
      show_drivers: tenantAccountingSettings?.invoice_description?.show_drivers || false,
      show_total_empty_miles:
        tenantAccountingSettings?.invoice_description?.show_total_empty_miles || false,
      show_total_loaded_miles:
        tenantAccountingSettings?.invoice_description?.show_total_loaded_miles || false,
      show_tags: tenantAccountingSettings?.invoice_description?.show_tags || false,
      show_order_notes: tenantAccountingSettings?.invoice_description?.show_order_notes || false
    });

    setSelectedDefaultInvoiceService(() => {
      let defaultService = null;
      invoiceServicesData.content.forEach((item: { value: InvoiceService }) => {
        if ((item.value as InvoiceService)?.isDefault) {
          defaultService = item;
        }
      });
      return defaultService;
    });

    const invoiceSubjectLine = invoiceSubjectLineOptions.find(
      (item) => item.value === tenantAccountingSettings?.invoice_subject_line
    );
    setSelectedInvoiceEmailSubjectLine(invoiceSubjectLine);

    const defaultInvoiceDate = defaultInvoiceDateOptions.find(
      (item) => item.value === tenantAccountingSettings?.default_invoice_date
    );
    setSelectedDefaultInvoiceDate(defaultInvoiceDate);
    setSelectedQboCustomField(defaultCustomField);
    setSendAllInvoices(tenantAccountingSettings?.send_invoices_to_factoring_by_default || false);
    setDefaultInvoiceSendOption(tenantAccountingSettings?.select_invoice_send_option || false);
    setSalesTaxEnabled(tenantAccountingSettings?.sales_tax_enabled || false);

    // reset factoring company
    resetFactoring();
  };

  const resetFactoring = (): void => {
    if (!enableFactoring) return;

    setCompanyData(originalCompanyData);
    setHasFactoringChanges(false);
    setFactoringCompany(
      getFactoringCompanyToOption(factoringCompanies, originalCompanyData?.company || 'none')
    );
  };

  const update = (): void => {
    setPrefixError(null);
    const selectedDefaultInvoiceServiceId = selectedDefaultInvoiceService?.id;

    if (useDivisionPrefix && !invoicePrefix) {
      setPrefixError(
        'You must enter a prefix for the invoice numbers when custom prefixes option is enabled, or disable it.'
      );
      // scroll to the prefix field and set on center
      prefixRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      addNotification({
        timeToShow: 5000,
        message:
          'You must enter a prefix for the invoice numbers when custom prefixes option is enabled, or disable it.',
        variant: 'error'
      });
      return;
    }

    const body: Partial<TenantAccountingSettings> = {
      ...defaultRecipients,
      default_invoice_service_id: !isQboEnabled
        ? selectedDefaultInvoiceServiceId
        : tenantAccountingSettings?.default_invoice_service_id,
      default_qbo_invoice_service_id: isQboEnabled
        ? selectedDefaultInvoiceServiceId
        : tenantAccountingSettings?.default_qbo_invoice_service_id,
      invoice_subject_line:
        (selectedInvoiceEmailSubjectLine?.value as InvoiceSubjectLine) ||
        InvoiceSubjectLine.INVOICE_NUMBER,
      default_invoice_date: (selectedDefaultInvoiceDate?.value as DefaultInvoiceDate) || undefined,
      qbo_customer_number_custom_field: selectedQboCustomField?.id,
      send_invoices_to_factoring_by_default: sendAllInvoices,
      select_invoice_send_option: defaultInvoiceSendOption,
      invoice_prefix: invoicePrefix || null,
      invoice_email_body: invoiceEmailBody || null,
      sales_tax_enabled: salesTaxEnabled
    };

    if (accountingEmail) body.accounting_email = accountingEmail;
    if (additionalRecipients.length > 0) body.additional_recipients = additionalRecipients;

    body.division_id = divisionId;
    body.invoice_description = { ...defaultInvoiceDescription };

    upsertTenantAccountSettings(body as TenantAccountingSettings)
      .unwrap()
      .then(() => {
        addNotification({
          message: 'Settings updated successfully',
          variant: 'success'
        });

        refreshCurrentUser();

        setHasChanges(false);
        updateSettingsTab(SettingsSection.appSettings);
      })
      .catch((error) => {
        addNotification({
          message: error?.data?.message || 'Failed to update settings',
          variant: 'error'
        });
      });

    // save factoring company
    if (enableFactoring && hasFactoringChanges) {
      saveFactoringConfig();
    }
  };

  const createInternalFactoringCompany = async (): Promise<void> => {
    const { company, company_address, company_email } = companyData as IOtherCompanies;
    const data = {
      name: company,
      email_address: company_email,
      address: company_address
    };

    createInternalFactoring({ divisionId, data })
      .finally(() => setLoadingFactoring(false))
      .then(() => {
        addNotification({
          message: 'Internal factoring company created successfully',
          variant: 'success'
        });
      })
      .catch((error: { data: { message: any } }) => {
        addNotification({
          message: error.data.message,
          variant: 'error'
        });
      });
  };

  /**
   * Handles the values update for the recipients settings
   */
  const handleRecipientsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let checkboxToUpdate: Partial<ITenantDefaultRecipients> = {};
    switch (e.target.name) {
      case 'to_cust_contact_email':
        checkboxToUpdate = { to_cust_contact_email: e.target.checked };
        break;
      case 'to_cust_billing_email':
        checkboxToUpdate = { to_cust_billing_email: e.target.checked };
        break;
      case 'cc_user_email':
        checkboxToUpdate = { cc_user_email: e.target.checked };
        break;
      case 'cc_accounting_email':
        checkboxToUpdate = { cc_accounting_email: e.target.checked };
        break;
    }

    const updated = {
      ...defaultRecipients,
      ...checkboxToUpdate
    };
    setDefaultRecipients(updated);
  };

  /**
   * Handles the values update for the invoice description settings
   */
  const handleInvoiceDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let checkboxToUpdate: Partial<IInvoiceDefaultDescription> = {};
    switch (e.target.name) {
      case 'pickup_stops':
        checkboxToUpdate = { show_pickup_stops: e.target.checked };
        break;
      case 'drop_off_stops':
        checkboxToUpdate = { show_drop_off_stops: e.target.checked };
        break;
      case 'transfer_stops':
        checkboxToUpdate = { show_transfer_stops: e.target.checked };
        break;
      case 'unspecific_stops':
        checkboxToUpdate = { show_unspecific_stops: e.target.checked };
        break;
      case 'truckbase_load_number':
        checkboxToUpdate = { show_truckbase_load_number: e.target.checked };
        break;
      case 'customer_load_number':
        checkboxToUpdate = { show_customer_load_number: e.target.checked };
        break;
      case 'load_dates':
        checkboxToUpdate = { show_load_dates: e.target.checked };
        break;
      case 'stop_name':
        checkboxToUpdate = { show_stop_name: e.target.checked };
        break;
      case 'stop_address':
        checkboxToUpdate = { show_stop_address: e.target.checked };
        break;
      case 'pickup_and_delivery_numbers':
        checkboxToUpdate = { show_pickup_and_delivery_numbers: e.target.checked };
        break;
      case 'commodities':
        checkboxToUpdate = { show_commodities: e.target.checked };
        break;
      case 'trucks':
        checkboxToUpdate = { show_trucks: e.target.checked };
        break;
      case 'trailers':
        checkboxToUpdate = { show_trailers: e.target.checked };
        break;
      case 'drivers':
        checkboxToUpdate = { show_drivers: e.target.checked };
        break;
      case 'total_empty_miles':
        checkboxToUpdate = { show_total_empty_miles: e.target.checked };
        break;
      case 'total_loaded_miles':
        checkboxToUpdate = { show_total_loaded_miles: e.target.checked };
        break;
      case 'tags':
        checkboxToUpdate = { show_tags: e.target.checked };
        break;
      case 'order_notes':
        checkboxToUpdate = { show_order_notes: e.target.checked };
        break;
    }

    const updatedInvoiceSettings = {
      ...defaultInvoiceDescription,
      ...checkboxToUpdate
    };
    setDefaultInvoiceDescription(updatedInvoiceSettings);
  };

  const handleInvoicePrefixChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = e.target.checked;
    if (!checked) {
      setPrefixError(null);
    }
    setUseDivisionPrefix(checked);
  };

  const handleInvoiceSendOptionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDefaultInvoiceSendOption(e.target.checked);
  };

  const handleSalesTaxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSalesTaxEnabled(e.target.checked);
  };

  useEffect(() => {
    if (
      tenantAccountingSettings &&
      (accountingEmail !== tenantAccountingSettings.accounting_email ||
        defaultRecipients.cc_accounting_email !== tenantAccountingSettings.cc_accounting_email ||
        defaultRecipients.cc_user_email !== tenantAccountingSettings.cc_user_email ||
        defaultRecipients.to_cust_billing_email !==
          tenantAccountingSettings.to_cust_billing_email ||
        defaultRecipients.to_cust_contact_email !==
          tenantAccountingSettings.to_cust_contact_email ||
        defaultInvoiceDescription.show_drop_off_stops !==
          tenantAccountingSettings.invoice_description.show_drop_off_stops ||
        defaultInvoiceDescription.show_pickup_stops !==
          tenantAccountingSettings.invoice_description.show_pickup_stops ||
        defaultInvoiceDescription.show_transfer_stops !==
          tenantAccountingSettings.invoice_description.show_transfer_stops ||
        defaultInvoiceDescription.show_unspecific_stops !==
          tenantAccountingSettings.invoice_description.show_unspecific_stops ||
        defaultInvoiceDescription.show_customer_load_number !==
          tenantAccountingSettings.invoice_description.show_customer_load_number ||
        defaultInvoiceDescription.show_stop_name !==
          tenantAccountingSettings.invoice_description.show_stop_name ||
        defaultInvoiceDescription.show_stop_address !==
          tenantAccountingSettings.invoice_description.show_stop_address ||
        defaultInvoiceDescription.show_load_dates !==
          tenantAccountingSettings.invoice_description.show_load_dates ||
        defaultInvoiceDescription.show_pickup_and_delivery_numbers !==
          tenantAccountingSettings.invoice_description.show_pickup_and_delivery_numbers ||
        defaultInvoiceDescription.show_truckbase_load_number !==
          tenantAccountingSettings.invoice_description.show_truckbase_load_number ||
        defaultInvoiceDescription.show_commodities !==
          tenantAccountingSettings.invoice_description.show_commodities ||
        defaultInvoiceDescription.show_trucks !==
          tenantAccountingSettings.invoice_description.show_trucks ||
        defaultInvoiceDescription.show_trailers !==
          tenantAccountingSettings.invoice_description.show_trailers ||
        defaultInvoiceDescription.show_drivers !==
          tenantAccountingSettings.invoice_description.show_drivers ||
        defaultInvoiceDescription.show_total_empty_miles !==
          tenantAccountingSettings.invoice_description.show_total_empty_miles ||
        defaultInvoiceDescription.show_total_loaded_miles !==
          tenantAccountingSettings.invoice_description.show_total_loaded_miles ||
        defaultInvoiceDescription.show_tags !==
          tenantAccountingSettings.invoice_description.show_tags ||
        defaultInvoiceDescription.show_order_notes !==
          tenantAccountingSettings.invoice_description.show_order_notes ||
        (selectedDefaultInvoiceService &&
          (selectedDefaultInvoiceService?.value as InvoiceService)?.id !==
            (tenantAccountingSettings?.default_invoice_service_id
              ? tenantAccountingSettings?.default_invoice_service_id
              : tenantAccountingSettings?.default_qbo_invoice_service_id)) ||
        selectedInvoiceEmailSubjectLine?.value !== tenantAccountingSettings?.invoice_subject_line ||
        (selectedDefaultInvoiceDate &&
          selectedDefaultInvoiceDate?.value !== tenantAccountingSettings?.default_invoice_date) ||
        (selectedDefaultInvoiceDate &&
          selectedDefaultInvoiceDate?.value !== tenantAccountingSettings?.default_invoice_date) ||
        (isQboEnabled &&
          selectedQboCustomField?.id !==
            tenantAccountingSettings?.qbo_customer_number_custom_field) ||
        tenantAccountingSettings?.send_invoices_to_factoring_by_default !== sendAllInvoices ||
        JSON.stringify(additionalRecipients) !==
          JSON.stringify(tenantAccountingSettings?.additional_recipients))
    ) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [
    accountingEmail,
    defaultRecipients,
    defaultInvoiceDescription,
    selectedDefaultInvoiceService,
    selectedInvoiceEmailSubjectLine,
    selectedDefaultInvoiceDate,
    selectedQboCustomField,
    sendAllInvoices
  ]);

  const handleQBOConnect = async (): Promise<void> => {
    if (!isSignedInQbo) {
      try {
        const QBOUrl = await QBOService.signIn(divisionId);
        dispatch(
          twApi.util.invalidateTags(['TenantAccountingSettings', 'RateTables', 'InvoiceService'])
        );
        redirect(QBOUrl.url, false);
      } catch (error) {
        logRocketTrack('QBO Integration', {
          action: 'Trying to connect to QBO',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Unable to connect to Quickbooks Online',
          variant: 'error',
          actionOptions: {
            action: 'Retry',
            onActionClick: handleQBOConnect
          }
        });
      }
    } else {
      try {
        await QBOService.signOut(divisionId);
        dispatch(
          twApi.util.invalidateTags(['TenantAccountingSettings', 'RateTables', 'InvoiceService'])
        );
        refetchDivisionsData();
        addNotification({
          message: 'Disconnected from Quickbooks Online',
          variant: 'success'
        });
      } catch (error) {
        logRocketTrack('QBO Integration', {
          action: 'Disconnect QBO integration',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Unable to disconnect from Quickbooks Online',
          variant: 'error',
          actionOptions: {
            action: 'Retry',
            onActionClick: handleQBOConnect
          }
        });
      }
    }
  };

  const handleQbDesktopConnect = async (): Promise<void> => {
    if (!qbDesktopSettings[divisionId]?.doesConfigExistInDb) {
      try {
        const qbdUrl = await QbDesktopService.connect(divisionId);
        dispatch(
          twApi.util.invalidateTags([
            'TenantAccountingSettings',
            'RateTables',
            'InvoiceService',
            'QbDesktop'
          ])
        );
        redirect(qbdUrl, false);
      } catch (error) {
        logRocketTrack('QBD Integration', {
          action: 'Trying to connect to QBD',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        console.error('qbd', error);
        addNotification({
          message: (error as any).response?.data?.message || 'Unable to connect to Quickbooks Desktop',
          variant: 'error',
        });
      }
    } else {
      try {
        await QbDesktopService.disconnect(divisionId);
        dispatch(
          twApi.util.invalidateTags([
            'TenantAccountingSettings',
            'RateTables',
            'InvoiceService',
            'QbDesktop'
          ])
        );
        refetchQbDesktopConfig();
        addNotification({
          message: 'Disconnected from Quickbooks Desktop',
          variant: 'success'
        });
      } catch (error) {
        logRocketTrack('QBD Integration', {
          action: 'Disconnect QBD integration',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Unable to disconnect from Quickbooks Desktop',
          variant: 'error',
          actionOptions: {
            action: 'Retry',
            onActionClick: handleQbDesktopConnect
          }
        });
      }
    }
  };

  const handleQbDesktopToggleFieldMapping = (): void => {
    setIsQbDesktopFieldMappingOpen(!isQbDesktopFieldMappingOpen);
  };

  const downloadFile = (blob: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  };

  const handleDownloadObjInfo = (type: 'qbd' | 'tb'): void => {
    if (type === 'qbd') {
      setIsQbDesktopDlObjInfoLoading(true);
      QbDesktopService.getQbDesktopObjectsCsv(divisionId)
        .then((blob) => {
          downloadFile(blob, 'qbd_objects.csv');
        })
        .catch((err) => {
          console.error('Error downloading QBD objects', err);
          addNotification({
            message: 'Error downloading QBD objects',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsQbDesktopDlObjInfoLoading(false);
        });
    } else {
      setIsTbDlObjInfoLoading(true);
      QbDesktopService.getTbObjectsCsv(divisionId)
        .then((blob) => {
          downloadFile(blob, 'tb_objects.csv');
        })
        .catch((err) => {
          console.error('Error downloading TB objects', err);
          addNotification({
            message: 'Error downloading TB objects',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsTbDlObjInfoLoading(false);
        });
    }
  };

  const handleQbDesktopSaveFieldMapping = async (
    fieldMapping: QbDesktopFieldMapping
  ): Promise<void> => {
    await QbDesktopService.updateFieldMapping(divisionId, fieldMapping);
  };

  const handleQbDesktopAutoSyncChange = async (): Promise<void> => {
    try {
      const newValue = !isQbDesktopAutoSyncEnabled;
      await QbDesktopService.updateAutoSync(divisionId, newValue);
      setIsQbDesktopAutoSyncEnabled(newValue);
      refetchQbDesktopConfig();
    } catch (error) {
      addNotification({
        message: 'Failed to update setting',
        variant: 'error'
      });
    }
  };

  const handleQbDesktopAutoCustomerCreateChange = async (): Promise<void> => {
    try {
      const newValue = !isQbDesktopAutoCustomerCreateEnabled;
      await QbDesktopService.updateAutoCustomerCreate(divisionId, newValue);
      setIsQbDesktopAutoCustomerCreateEnabled(newValue);
      refetchQbDesktopConfig();
    } catch (error) {
      addNotification({
        message: 'Failed to update setting',
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    const updateIntegration = async (): Promise<void> => {
      try {
        await QBOService.callback(query as string);
        refetchAccountingSettings();
        refetchDivisionsData();
        setIsLoading(false);
        addNotification({
          message: 'Connected to Quickbooks Online',
          variant: 'success'
        });
      } catch (err) {
        setIsLoading(false);
        logRocketTrack('QBO Integration', {
          action: 'Trying to connect to QBO',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Unable to connect to Quickbooks Online',
          variant: 'error',
          actionOptions: {
            action: 'Retry',
            onActionClick: handleQBOConnect
          }
        });
      }
    };
    if (!!query && !isSignedInQbo && !isLoading) {
      const searchParams = new URLSearchParams(query);
      const state = searchParams.get('state');
      const divisionId = JSON.parse(state || '{}').division_id;
      setDivisionId(divisionId || userData!.defaultDivision?.id);
      (async (): Promise<void> => {
        setIsLoading(true);
        await updateIntegration();
      })();
    }
  }, [query]);

  useEffect(() => {
    if (!useDivisionPrefix) {
      setInvoicePrefix(null);
    }
  }, [useDivisionPrefix]);

  const hasFactoringChanged = (newData: INamedCompanies | IOtherCompanies | null): boolean => {
    if (!_.isEqual(newData, originalCompanyData)) return true;

    return false;
  };

  const updateFactoringCompany = (
    selectedCompany: FactoringOption,
    data: INamedCompanies | IOtherCompanies | null,
    sendByDefault: boolean
  ): void => {
    hasFactoringChanged(data) ? setHasFactoringChanges(true) : setHasFactoringChanges(false);
    setFactoringCompany(selectedCompany);
    setCompanyData(data);
    setSendAllInvoices(sendByDefault);
  };

  return (
    <Box className="invoicing">
      {enableQbDesktop && (
        <TwQbDesktopFieldMapping
          isOpen={isQbDesktopFieldMappingOpen}
          onClose={(): void => {
            setIsQbDesktopFieldMappingOpen(false);
          }}
          onSave={handleQbDesktopSaveFieldMapping}
          fieldMapping={qbDesktopDivisionFieldMapping}
        />
      )}
      {divisionsEnabled && (
        <Box className="invoicing__divisions">
          {divisions.content.map((division: { id: any; name: any; is_default_division: any }) => (
            <DivisionTab
              key={division.id}
              selected={division.id === divisionId}
              onClick={(): void => {
                setDivisionId(division.id);
              }}
              // I added the styling below to show the Your Company label next to the division name but feel free to clean up this code if there's a better way to do this - LR
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {' '}
                {/* Ensure alignment and spacing */}
                {division.name}
                {division.is_default_division && (
                  <Typography
                    variant="subtitle2"
                    fontWeight="400"
                    color="#9CA3AF"
                    fontSize={'13px'}
                  >
                    {'Your company'}
                  </Typography>
                )}
              </span>
            </DivisionTab>
          ))}
        </Box>
      )}
      <Stack direction="row">
        <Stack width="310px" flexShrink={0}>
          <Typography variant="h5" className="app-settings__subtitle">
            Invoicing & accounting
          </Typography>
          <Typography className="settings__subtitle">
            Manage your invoicing and accounting settings
          </Typography>
        </Stack>

        <Stack width="65%" className="invoicing__content">
          <Stack className="invoicing__settings-container">
            <Typography className="invoicing__subtitle">QuickBooks</Typography>
            {!isSignedInQbo ? (
              <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
                Connect to your QuickBooks account to be able to use it for invoicing.
                <br />
                You can only have one active account.
              </Typography>
            ) : (
              <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
                Connected to QuickBooks Online via{' '}
                <Typography
                  variant="body1"
                  fontWeight="600"
                  color="#7e858e"
                  fontSize="14px"
                  component={'span'}
                >
                  {tenantAccountingSettings?.division_qbo_credentials?.company_name}
                </Typography>
              </Typography>
            )}

            <Stack
              direction="column"
              display={
                qbDesktopSettings[divisionId]?.doesConfigExistInDb &&
                qbButtonChoice === 'QB_DESKTOP'
                  ? 'inherit'
                  : 'none'
              }
            >
              <Stack direction="row">
                <TwButton
                  label="Configure field mapping"
                  sx={{ width: 'fit-content', padding: '0 8px 0 0' }}
                  type={TwButtonType.text}
                  action={(): void => {
                    handleQbDesktopToggleFieldMapping();
                  }}
                />{' '}
                |
                <TwButton
                  label="Download QBD Object Info"
                  sx={{ width: 'fit-content', padding: '0 8px' }}
                  type={TwButtonType.text}
                  action={(): void => {
                    handleDownloadObjInfo('qbd');
                  }}
                  startIcon={
                    isQbDesktopDlObjInfoLoading ? <CircularProgress size={20} /> : <DownloadIcon />
                  }
                  disabled={
                    isQbDesktopDlObjInfoLoading ||
                    !qbDesktopSettings[divisionId]?.isConnectedToConductor
                  }
                />
                |
                <TwButton
                  label="Download TB Object Info"
                  sx={{ width: 'fit-content', padding: '0 8px' }}
                  type={TwButtonType.text}
                  action={(): void => {
                    handleDownloadObjInfo('tb');
                  }}
                  startIcon={
                    isTbDlObjInfoLoading ? <CircularProgress size={20} /> : <DownloadIcon />
                  }
                  disabled={
                    isTbDlObjInfoLoading ||
                    !qbDesktopSettings[divisionId]?.isConnectedToConductor
                  }
                />
              </Stack>
              <FormControlLabel
                value="end"
                control={
                  <Switch
                    checked={isQbDesktopAutoCustomerCreateEnabled}
                    onChange={handleQbDesktopAutoCustomerCreateChange}
                    disabled={isQbDesktopAutoCustomerCreateEnabled === undefined}
                  />
                }
                label={'Create new customer in QBD if customer not found in sync'}
              />
              <FormControlLabel
                value="end"
                control={
                  <Switch
                    checked={isQbDesktopAutoSyncEnabled}
                    onChange={handleQbDesktopAutoSyncChange}
                    disabled={isQbDesktopAutoSyncEnabled === undefined}
                  />
                }
                label={'Enable automatic invoice synchronization'}
              />
            </Stack>

            <Stack direction="row" marginBottom="15px" marginTop="10px" spacing={1}>
              <>
                {qbButtonChoice === 'LOADING' && (
                  <Stack direction="row" spacing={1} marginTop="10px">
                    <CircularProgress size={14} />
                    <Typography
                      variant="subtitle2"
                      fontWeight="500"
                      color="#7e858e"
                      fontSize="14px"
                    >
                      Loading...
                    </Typography>
                  </Stack>
                )}
                {(qbButtonChoice === 'QB_ONLINE' || qbButtonChoice === 'ALL') && (
                  <TwButton
                    type={TwButtonType.secondary}
                    label={isSignedInQbo ? 'Disconnect from QBO' : 'Connect to QBO'}
                    startIcon={<QBLogo />}
                    action={(): void => {
                      handleQBOConnect();
                    }}
                  />
                )}
                {(qbButtonChoice === 'QB_DESKTOP' || qbButtonChoice === 'ALL') && (
                  <TwTooltip title={qbDesktopSettings[divisionId]?.errorMessage}>
                    <TwButton
                      type={TwButtonType.secondary}
                      label={
                        qbDesktopSettings[divisionId] === undefined
                          ? 'Loading...'
                          : qbDesktopSettings[divisionId]?.doesConfigExistInDb
                          ? 'Disconnect from QBD'
                          : 'Connect to QBD'
                      }
                      startIcon={
                        qbDesktopSettings[divisionId] === undefined ? (
                          <CircularProgress size={20} />
                        ) : qbDesktopSettings[divisionId]?.doesConfigExistInDb &&
                          !qbDesktopSettings[divisionId]?.isConnectedToConductor ? (
                          <ErrorIcon sx={{ color: '#EF4444' }} />
                        ) : (
                          <QBLogo />
                        )
                      }
                      action={(): void => {
                        handleQbDesktopConnect();
                      }}
                      disabled={qbDesktopSettings[divisionId] === undefined}
                    />
                  </TwTooltip>
                )}
              </>
            </Stack>
          </Stack>

          {enableFactoring && (
            <>
              <TwDivider />
              <Stack className="invoicing__settings-container">
                <Factoring
                  factoringCompanies={factoringCompanies}
                  factoringCompany={factoringCompany}
                  sendAllInvoices={sendAllInvoices}
                  loading={loadingFactoring}
                  companyData={companyData}
                  requiredFields={factoringCompany.requiredFields}
                  handleUpdateFactoringCompany={updateFactoringCompany}
                  hasFactoringError={hasErrorFactoring}
                />
              </Stack>
            </>
          )}

          <TwDivider />
          <Stack className="invoicing__settings-container">
            <Typography className="invoicing__subtitle">General</Typography>
            <TwField
              id="accounting_email"
              name="accounting_email"
              value={accountingEmail}
              label="Accounting email"
              inputType={TwInputTypes.text}
              onInputChange={handleEmailChange}
            />
            <Stack direction="column" spacing="0" marginBottom={'8px'}>
              <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
                Define linehaul service
              </Typography>
              <TwAutocomplete
                name={`service`}
                optionList={invoiceServicesData.content}
                value={selectedDefaultInvoiceService}
                sortList={false}
                error={!selectedDefaultInvoiceService}
                onChange={(_: any, newValue: any): void => {
                  if (!newValue) {
                    setSelectedDefaultInvoiceService(null);
                    return;
                  }
                  setSelectedDefaultInvoiceService(newValue);
                }}
                enableClearIcon={false}
                disableClearable={true}
              />
            </Stack>
            <Stack direction="column" spacing="0" marginBottom={'8px'}>
              <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
                ID number in invoice email subject line
              </Typography>
              <TwAutocomplete
                name={`invoice-subject-line`}
                optionList={invoiceSubjectLineOptions}
                value={selectedInvoiceEmailSubjectLine}
                sortList={false}
                error={false}
                onChange={(_: any, newValue: any): void => {
                  if (!newValue) {
                    setSelectedInvoiceEmailSubjectLine(invoiceSubjectLineOptions[0]);
                    return;
                  }
                  setSelectedInvoiceEmailSubjectLine(newValue);
                }}
              />
            </Stack>

            <Stack direction="column" spacing="0" marginBottom={'8px'}>
              <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
                Default invoice date (Truckbase invoices only)
              </Typography>
              <TwAutocomplete
                name={`default-invoice-date`}
                optionList={defaultInvoiceDateOptions}
                value={selectedDefaultInvoiceDate}
                sortList={false}
                error={false}
                disableClearable={true}
                onChange={(_: any, newValue: any): void => {
                  if (!newValue) {
                    setSelectedDefaultInvoiceDate(defaultInvoiceDateOptions[0]);
                    return;
                  }
                  setSelectedDefaultInvoiceDate(newValue);
                }}
              />
            </Stack>

            {isQboEnabled && showQboCustomField && (
              <Stack direction="column" spacing="0">
                <Typography variant="subtitle2" fontWeight="500" color="#7e858e" fontSize="14px">
                  Link Customer order # to a custom field in your QuickBooks account
                </Typography>
                <TwAutocomplete
                  name={`customer-load-number-qbo`}
                  optionList={qboCustomFieldsList}
                  value={selectedQboCustomField}
                  disableClearable={true}
                  onChange={(_: any, newValue: any): void => {
                    if (!newValue) {
                      return;
                    }
                    setSelectedQboCustomField(newValue);
                  }}
                />
              </Stack>
            )}

            {/* Default invoice send option */}
            {!isQboEnabled && (
              <Stack direction="column" spacing={1} marginBottom={'24px'}>
                <Box>
                  <TwSwitch
                    checked={defaultInvoiceSendOption}
                    onChange={handleInvoiceSendOptionChange}
                    label="Default invoice send option"
                  />
                  <Typography
                    className="settings__subtitle"
                    sx={{ marginTop: '2px', marginLeft: '48px' }}
                  >
                    Set the default behavior for the &quot;Send invoice&quot; checkbox on the
                    invoice creation page. When enabled, the checkbox will be selected by default,
                    allowing you to send invoices immediately after creation. Disable to create
                    invoice only and not email the invoice.
                  </Typography>
                </Box>
              </Stack>
            )}

            {!isQboEnabled && (
              <Stack direction="column" spacing={1} marginBottom={'24px'}>
                <Box>
                  <TwSwitch
                    checked={useDivisionPrefix}
                    onChange={handleInvoicePrefixChange}
                    label="Enable custom prefixes"
                  />
                  <Typography
                    className="settings__subtitle"
                    sx={{ marginTop: '2px', marginLeft: '48px' }}
                  >
                    Enable this setting to apply a custom invoice prefix to your invoices. Please
                    note, this will reset your invoice number to &quot;1&quot;. Contact support to
                    update your starting number.
                  </Typography>

                  {useDivisionPrefix && (
                    <Box sx={{ marginLeft: '48px', mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="500"
                        color="#7e858e"
                        fontSize="14px"
                        sx={{ mb: 1 }}
                      >
                        Prefix
                      </Typography>
                      <TwField
                        inputRef={prefixRef}
                        error={!!prefixError}
                        id="invoice-prefix"
                        value={invoicePrefix || ''}
                        name="invoice_prefix"
                        onInputChange={(e): void => {
                          const value = e.target.value.trim();
                          if (!/^[0-9a-zA-Z]+$/.test(value) && value !== '') {
                            return;
                          }

                          if (value.length <= 3) {
                            setPrefixError(null);
                            setInvoicePrefix(value);
                          }
                        }}
                        sx={{
                          width: 150
                        }}
                        placeholder="XXX"
                        disabled={!useDivisionPrefix}
                      />
                      <Typography className="settings__subtitle" sx={{ mt: 1 }}>
                        Contact support to customize the starting numbers
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>

          <TwDivider />
          <Stack direction="column" spacing="0" marginBottom={'8px'}>
            <TwSwitch
              checked={salesTaxEnabled}
              onChange={handleSalesTaxChange}
              label="Enable sales tax on invoices"
            />
            <Box>
              <Typography
                className="settings__subtitle"
                sx={{ marginTop: '2px', marginLeft: '48px' }}
              >
                Enable to be able to configure and apply sales taxes on invoices
              </Typography>
            </Box>
            <Box display={salesTaxEnabled ? 'block' : 'none'} paddingLeft={'48px'} paddingTop={'12px'}>
              <SalesTaxSettings divisionId={divisionId} />
            </Box>
          </Stack>

          <Stack className="invoicing__recipients-container">
            <Typography variant="h5" className="invoicing__subtitle">
              {`Default 'To' recipients`}
            </Typography>
            <Typography className="settings__subtitle">
              These emails will be added by default when sending Truckbase invoices. Quickbooks
              invoices will always have the customer billing email as the default.
            </Typography>
            <TwCheckbox
              name="to_cust_contact_email"
              value="to_cust_contact_email"
              label="Customer contact email"
              checked={defaultRecipients.to_cust_contact_email}
              onInputChange={handleRecipientsCheckboxChange}
            />
            <TwCheckbox
              name="to_cust_billing_email"
              value="to_cust_billing_email"
              label="Customer billing email"
              checked={defaultRecipients.to_cust_billing_email}
              onInputChange={handleRecipientsCheckboxChange}
            />
          </Stack>
          <TwDivider />
          <Stack className="invoicing__recipients-container">
            <Typography variant="h5" className="invoicing__subtitle">
              {`Default additional recipients`}
            </Typography>
            <Typography className="settings__subtitle">{`This setting will populate the 'Cc' field for Truckbase invoices, and the 'Add additional recipients' field for Quickbooks invoices`}</Typography>
            <TwCheckbox
              name="cc_user_email"
              value="cc_user_email"
              label="User email"
              onInputChange={handleRecipientsCheckboxChange}
              checked={defaultRecipients.cc_user_email}
            />
            <TwCheckbox
              name="cc_accounting_email"
              value="cc_accounting_email"
              label="Accounting email"
              onInputChange={handleRecipientsCheckboxChange}
              checked={defaultRecipients.cc_accounting_email}
            />
            <TwTagsField
              id="additional-invoice-recipients"
              label="Additional invoice recipients"
              tagsDisplayed={additionalRecipients}
              tagType={'email'}
              onTagAdded={(emails: any): void => {
                setAdditionalRecipients(emails);
              }}
            />
          </Stack>

          <TwDivider />
          {/* Customize invoice description settings */}
          <Stack className="invoicing__invoice-description-container">
            <Typography variant="h5" className="invoicing__subtitle">
              {`Customize your invoice description`}
            </Typography>
            <Typography className="settings__subtitle">{`Select which stop types and load details to include in invoice description`}</Typography>
            <TwCheckbox
              name="pickup_stops"
              value="show_pickup_stops"
              label="Pickup stops"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_pickup_stops}
            />
            <TwCheckbox
              name="drop_off_stops"
              value="show_drop_off_stops"
              label="Drop off stops"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_drop_off_stops}
            />
            <TwCheckbox
              name="transfer_stops"
              value="show_transfer_stops"
              label="Transfer stops"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_transfer_stops}
            />
            <TwCheckbox
              name="unspecific_stops"
              value="show_unspecific_stops"
              label="Stops without a specified type"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_unspecific_stops}
            />
            <TwCheckbox
              name="truckbase_load_number"
              value="show_truckbase_load_number"
              label="Truckbase load number"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_truckbase_load_number}
            />
            <TwCheckbox
              name="customer_load_number"
              value="show_customer_load_number"
              label="Customer load number"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_customer_load_number}
            />
            <TwCheckbox
              name="load_dates"
              value="show_load_dates"
              label="Date(s) of stops"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_load_dates}
            />
            <TwCheckbox
              name="stop_name"
              value="show_stop_name"
              label="Stop name"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_stop_name}
            />
            <TwCheckbox
              name="stop_address"
              value="show_stop_address"
              label="Stop address"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_stop_address}
            />
            <TwCheckbox
              name="trucks"
              value="show_trucks"
              label="Truck(s)"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_trucks}
            />
            <TwCheckbox
              name="drivers"
              value="show_drivers"
              label="Driver(s)"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_drivers}
            />
            <TwCheckbox
              name="pickup_and_delivery_numbers"
              value="show_pickup_and_delivery_numbers"
              label="Pickup/Delivery numbers"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_pickup_and_delivery_numbers}
            />
            {showCommoditiesDescriptionCheckbox && (
              <TwCheckbox
                name="commodities"
                value="show_commodities"
                label="Commodities"
                onInputChange={handleInvoiceDescriptionChange}
                checked={defaultInvoiceDescription.show_commodities}
              />
            )}
            <TwCheckbox
              name="trailers"
              value="show_trailers"
              label="Trailer(s)"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_trailers}
            />
            <TwCheckbox
              name="total_empty_miles"
              value="show_total_empty_miles"
              label="Total empty miles"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_total_empty_miles}
            />
            <TwCheckbox
              name="total_loaded_miles"
              value="show_total_loaded_miles"
              label="Total loaded miles"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_total_loaded_miles}
            />
            <TwCheckbox
              name="tags"
              value="show_tags"
              label="Tag(s)"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_tags}
            />
            <TwCheckbox
              name="order_notes"
              value="show_order_notes"
              label="Order Notes"
              onInputChange={handleInvoiceDescriptionChange}
              checked={defaultInvoiceDescription.show_order_notes}
            />
          </Stack>

          <TwDivider />

          <Stack className="invoicing__invoice-email-template-container">
            <Typography variant="h5" className="invoicing__subtitle">
              Invoice email template
            </Typography>
            <Typography
              sx={{
                color: '#6B7280',
                mb: 2,
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0px'
              }}
            >
              This template will be used when sending all invoices in Truckbase
            </Typography>
            <Typography
              sx={{
                color: '#6B7280',
                mb: 1,
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0px'
              }}
            >
              Email body
            </Typography>
            <Box
              sx={{
                width: '100%',
                minHeight: '200px',
                height: 'auto',
                bgcolor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                p: 2
              }}
            >
              <Typography
                sx={{
                  color: '#9CA3AF',
                  mb: 2,
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0px'
                }}
              >
                Hello [customer],
              </Typography>

              <TextField
                multiline
                minRows={4}
                maxRows={20}
                fullWidth
                value={invoiceEmailBody}
                onChange={(e): void => {
                  setInvoiceEmailBody(e.target.value);
                  setHasChanges(true);
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#FFFFFF',
                    padding: 0,
                    height: 'auto',
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '24px',
                    letterSpacing: '0px',
                    '& fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    p: 0,
                    color: '#111827',
                    height: 'auto',
                    overflow: 'hidden',
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '24px',
                    letterSpacing: '0px'
                  },
                  '& .MuiInputBase-root': {
                    padding: 0,
                    height: 'auto',
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '24px',
                    letterSpacing: '0px'
                  }
                }}
              />

              <Typography
                sx={{
                  color: '#9CA3AF',
                  mt: 2,
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0px'
                }}
              >
                Sincerely,
                <br />
                [contact, carrier name]
              </Typography>
            </Box>
          </Stack>

          {enableFuelcardIntegration && (
            <>
              <TwDivider />
              <FuelcardAccountManagerSetting divisionId={divisionId} />
            </>
          )}
          {(hasChanges || hasFactoringChanges) && (
            <div className="account__actions">
              <TwButton type={TwButtonType.secondary} label="Discard" action={discardChanges} />
              <TwButton type={TwButtonType.primary} label="Save changes" action={update} />
            </div>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default InvoicingAndAccounting;

const DivisionTab = styled('button')<{ selected?: boolean }>(({ selected }) => ({
  display: 'flex',
  padding: '8px 12px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: selected ? '#FFF' : 'transparent',
  border: selected ? '1px solid #E5E7EB' : 'none',
  borderRadius: '6px',
  boxShadow: selected ? '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' : 'none',
  color: selected ? '#1F2937' : '#6B7280',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  cursor: 'pointer'
}));
